import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Rating,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import axios from 'axios';
import { API_URL } from '../config';
import ReCAPTCHA from "react-google-recaptcha";

// Создаем отдельный экземпляр axios для публичных запросов
const publicApi = axios.create({
  baseURL: API_URL
});

// Константа для reCAPTCHA
const RECAPTCHA_SITE_KEY = '6LdbUQMrAAAAANQ0qoqh-3AXEAmR2_9RyEeJH9IJ';

function TestReview() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [rating, setRating] = useState(5);
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [reviews, setReviews] = useState([]);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  // Загрузка списка продуктов
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await publicApi.get("/products");
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        showAlert("Ошибка при загрузке продуктов", "error");
      }
    };
    fetchProducts();
  }, []);

  // Загрузка отзывов для выбранного продукта
  useEffect(() => {
    const fetchReviews = async () => {
      if (selectedProduct) {
        try {
          const response = await publicApi.get(`/reviews/product/${selectedProduct}`);
          setReviews(response.data);
        } catch (error) {
          console.error("Error fetching reviews:", error);
          showAlert("Ошибка при загрузке отзывов", "error");
        }
      }
    };
    fetchReviews();
  }, [selectedProduct]);

  const showAlert = (message, severity = "info") => {
    setAlert({
      show: true,
      message,
      severity,
    });
  };

  // Запрос кода подтверждения
  const handleRequestCode = async () => {
    if (!email) {
      showAlert("Пожалуйста, введите email", "warning");
      return;
    }

    setLoading(true);
    try {
      await publicApi.post("/reviews/request-code", { email });
      showAlert("Код подтверждения отправлен на вашу почту", "success");
      setVerificationDialogOpen(true);
    } catch (error) {
      console.error("Error requesting verification code:", error);
      showAlert("Ошибка при отправке кода подтверждения", "error");
    } finally {
      setLoading(false);
    }
  };

  // Проверка кода подтверждения
  const handleVerifyCode = async () => {
    if (!verificationCode) {
      showAlert("Пожалуйста, введите код подтверждения", "warning");
      return;
    }

    setLoading(true);
    try {
      console.log('Verifying code:', { email, verificationCode });
      
      const response = await publicApi.post("/reviews/verify-code", {
        email,
        verificationCode,
      });
      
      console.log('Verification response:', response.data);
      
      showAlert("Email успешно подтвержден", "success");
      setVerificationDialogOpen(false);
      setIsEmailVerified(true);
      // Сохраняем код подтверждения для последующей отправки
      setVerificationCode(verificationCode);
    } catch (error) {
      console.error("Error verifying code:", error);
      console.error("Error details:", error.response?.data);
      showAlert(error.response?.data?.message || "Неверный код подтверждения", "error");
    } finally {
      setLoading(false);
    }
  };

  // Отправка отзыва
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Current state before submission:', {
      selectedProduct,
      email,
      rating,
      comment,
      verificationCode,
      isEmailVerified,
      recaptchaToken
    });

    if (!selectedProduct) {
      showAlert("Пожалуйста, выберите продукт", "warning");
      return;
    }

    if (!isEmailVerified) {
      showAlert("Пожалуйста, подтвердите ваш email", "warning");
      return;
    }

    if (!verificationCode) {
      showAlert("Отсутствует код подтверждения", "warning");
      return;
    }

    if (!recaptchaToken) {
      showAlert("Пожалуйста, пройдите проверку reCAPTCHA", "warning");
      return;
    }

    if (!rating || !comment.trim()) {
      showAlert("Пожалуйста, заполните все поля", "warning");
      return;
    }

    const reviewData = {
      productId: selectedProduct,
      email,
      rating: Number(rating),
      text: comment.trim(),
      verificationCode,
      recaptchaToken,
    };

    console.log('Sending review data:', reviewData);

    setLoading(true);
    try {
      const response = await publicApi.post("/reviews/public", reviewData);
      console.log('Server response:', response.data);

      showAlert("Отзыв успешно добавлен", "success");

      // Очистка формы
      setSelectedProduct("");
      setRating(5);
      setEmail("");
      setComment("");
      setIsEmailVerified(false);
      setVerificationCode("");
      setRecaptchaToken("");

      // Обновление списка отзывов
      if (selectedProduct) {
        const reviewsResponse = await publicApi.get(`/reviews/product/${selectedProduct}`);
        setReviews(reviewsResponse.data);
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      console.error("Error details:", error.response?.data);
      
      let errorMessage = "Ошибка при отправке отзыва";
      if (error.response?.data) {
        if (error.response.data.debug) {
          errorMessage = `${error.response.data.message} (Детали: ${JSON.stringify(error.response.data.debug)})`;
        } else {
          errorMessage = error.response.data.message;
        }
      }
      
      showAlert(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Оставить отзыв
      </Typography>

      {alert.show && (
        <Alert
          severity={alert.severity}
          sx={{ mb: 2 }}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Форма отзыва */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Выберите продукт</InputLabel>
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                required
                label="Выберите продукт"
              >
                {products.map((product) => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isEmailVerified}
              />
              <Button
                variant="contained"
                onClick={handleRequestCode}
                disabled={!email || isEmailVerified || loading}
              >
                {loading ? <CircularProgress size={24} /> : "Подтвердить email"}
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography component="legend">Оценка</Typography>
              <Rating
                value={rating}
                onChange={(e, newValue) => setRating(newValue)}
                size="large"
                required
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setRecaptchaToken(token)}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={!selectedProduct || !isEmailVerified || !recaptchaToken || loading}
            >
              {loading ? <CircularProgress size={24} /> : "Отправить отзыв"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Список отзывов */}
      <Typography variant="h6" gutterBottom>
        Отзывы о продукте
      </Typography>
      {reviews.length > 0 ? (
        reviews.map((review) => (
          <Card key={review._id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Rating value={review.rating} readOnly />
                <Typography sx={{ ml: 2, color: "text.secondary" }}>
                  {new Date(review.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              <Typography color="text.secondary" gutterBottom>
                {review.email}
              </Typography>
              <Typography>{review.text}</Typography>
            </CardContent>
          </Card>
        ))
      ) : (
        <Typography color="text.secondary">
          {selectedProduct
            ? "Нет отзывов для этого продукта"
            : "Выберите продукт, чтобы увидеть отзывы"}
        </Typography>
      )}

      {/* Диалог подтверждения */}
      <Dialog
        open={verificationDialogOpen}
        onClose={() => setVerificationDialogOpen(false)}
      >
        <DialogTitle>Подтверждение email</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Введите код подтверждения, отправленный на вашу почту
          </Typography>
          <TextField
            fullWidth
            label="Код подтверждения"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialogOpen(false)}>
            Отмена
          </Button>
          <Button 
            onClick={handleVerifyCode} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Подтвердить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TestReview;
